import React, { useState } from 'react';
import {
	Box,
	CircularProgress,
	IconButton,
	Link,
	Stack,
	TableCell,
	TableRow,
	Typography,
	useTheme,
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { formatDate } from '@/utils/constants';
import { CONSENT_STATUS } from '@/features/informedConsent/helpers';
import {
	InformedConsentStatusChip,
	CONSENT_STATUS_CHIP_SX,
} from '@/components/InformedConsentStatusChip';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { showToast } from '@/features/toast/toastSlice';
import { useDeleteInformedConsentMutation } from '@/features/clients/clientsApi';
import { updateConsentCache } from '@/features/informedConsent/consentCache';

export const InformedConsentHistoryTableRow = ({ record }) => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [deleteInformedConsent, { isLoading }] = useDeleteInformedConsentMutation();
	const isExpired = record.status === CONSENT_STATUS.EXPIRED;

	const handleDelete = async () => {
		setConfirmOpen(false);

		try {
			await deleteInformedConsent({
				informedConsentId: record.id,
				clientId: record.clientId,
			}).unwrap();
			await dispatch(updateConsentCache(record.clientId));

			dispatch(
				showToast({
					message: 'Informed consent deleted',
					success: true,
					duration: 5000,
					autohide: true,
				}),
			);
		} catch (error) {
			dispatch(
				showToast({
					message:
						error?.data ??
						error?.message ??
						'Failed to delete informed consent. Please try again.',
					error: true,
				}),
			);
		}
	};

	return (
		<>
			<TableRow>
				<TableCell
					sx={{
						color: isExpired
							? CONSENT_STATUS_CHIP_SX[CONSENT_STATUS.EXPIRED].color
							: 'text.primary',
					}}
				>
					{record.dateReceived ? formatDate(record.dateReceived, true) : '—'}
				</TableCell>
				<TableCell>
					<Typography variant='body2' color={!record.notes ? 'text.secondary' : undefined}>
						{record.notes ?? '—'}
					</Typography>
				</TableCell>
				<TableCell>
					{record.attachments?.length ? (
						<Stack spacing={0.5}>
							{record.attachments.map((attachment, attachmentIndex) => {
								const name =
									attachment.fileName ||
									attachment.name ||
									`Attachment ${attachmentIndex + 1}`;
								const href =
									attachment.webUrl || attachment.url || attachment.downloadUrl;

								if (!href) {
									return (
										<Typography key={`${record.id}-${name}`} variant='body2'>
											{name}
										</Typography>
									);
								}

								return (
									<Link
										key={`${record.id}-${attachment.fileId || name}`}
										href={href}
										target='_blank'
										rel='noopener noreferrer'
										underline='hover'
										sx={{
											display: 'inline-block',
											width: 'fit-content',
										}}
									>
										{name}
									</Link>
								);
							})}
						</Stack>
					) : (
						<Typography variant='body2' color='text.secondary'>
							—
						</Typography>
					)}
				</TableCell>
				<TableCell>
					<InformedConsentStatusChip status={record.status} />
				</TableCell>
				<TableCell align='right' sx={{ width: 48, px: 1 }}>
					{isLoading ? (
						<Box padding='0.76rem'>
							<CircularProgress size='1rem' />
						</Box>
					) : (
						<IconButton
							aria-label='delete'
							size='small'
							onClick={() => setConfirmOpen(true)}
							sx={{
								color: theme.palette.primary.main,
								position: 'relative',
								fontSize: '1.25rem !important',
							}}
						>
							<Delete size='small' sx={{ fontSize: '1.25rem !important' }} />
						</IconButton>
					)}
				</TableCell>
			</TableRow>
			<ConfirmDialog
				openDialog={confirmOpen}
				handleClose={() => setConfirmOpen(false)}
				handleConfirm={handleDelete}
				actionName='delete this informed consent'
				canBeUndone={false}
			/>
		</>
	);
};
